/**
 * Entry Store - Zustand store for journal entry state management.
 *
 * Local-first: all reads/writes go through the Dexie IndexedDB layer (localDb).
 * In-memory Zustand state is the fast UI mirror; IndexedDB is the durable source
 * of truth that survives reloads. There is no network dependency — the journal
 * works fully offline.
 *
 * Key behaviours:
 * - openEntry(date): loads an EXISTING entry's content (never blanks/duplicates).
 * - createEntry(date, content?): creates a new entry only if one doesn't exist;
 *   if it already exists it simply opens it (idempotent).
 * - updateEntry(date, content): optimistic in-memory update + debounced persist.
 * - loadEntries(): hydrates the metadata list from IndexedDB on mount.
 *
 * @see Requirements 8.1
 */

import { create } from 'zustand';
import { LocalJournalEntry, EntryMeta } from '@/types';
import { SyncStatus } from '@/types/enums';
import {
  getAllEntries,
  getEntry,
  putEntry as dbPutEntry,
  deleteEntry as dbDeleteEntry,
} from '@/services/localDb';
import { enqueueEntrySync, enqueueEntryDelete } from '@/services/entrySync';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Entry metadata plus a short plain-text preview for list/browse views. */
export interface EntryListItem extends EntryMeta {
  /** First ~160 chars of plain text extracted from the entry content. */
  preview: string;
}

export interface EntryState {
  /** The currently active/open journal entry. */
  currentEntry: LocalJournalEntry | null;
  /** Metadata + preview list of all user entries (for listing/browse). */
  entries: EntryListItem[];
  /** Whether an entry operation is in progress. */
  isLoading: boolean;
  /** Last error message from an entry operation. */
  error: string | null;
  /** Dates that have entries in the current calendar view. */
  calendarDates: string[];
}

export interface EntryActions {
  /** Set the currently active entry. */
  setCurrentEntry: (entry: LocalJournalEntry | null) => void;
  /** Load the list of entry metadata (with previews) from IndexedDB. */
  loadEntries: () => Promise<void>;
  /** Open an existing entry by date, loading its full content from IndexedDB. */
  openEntry: (entryDate: string) => Promise<void>;
  /**
   * Create a new entry for the given date. If an entry already exists for the
   * date, it is opened instead of being overwritten (idempotent / no duplicates).
   */
  createEntry: (entryDate: string, content?: string) => Promise<void>;
  /** Update the current entry content (optimistic + persisted). */
  updateEntry: (entryDate: string, content: string) => Promise<void>;
  /** Delete an entry by date (optimistic + persisted). */
  deleteEntry: (entryDate: string) => Promise<void>;
  /** Load calendar dates (dates that have entries) for a given year/month. */
  loadCalendarDates: (year: number, month: number) => Promise<void>;
  /** Clear any error state. */
  clearError: () => void;
}

export type EntryStore = EntryState & EntryActions;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract a short plain-text preview from a TipTap JSON content string.
 * Falls back gracefully for empty/invalid content.
 */
export function extractPreview(content: string, maxLen = 160): string {
  if (!content || content.trim() === '') return '';
  let doc: unknown;
  try {
    doc = JSON.parse(content);
  } catch {
    // Not JSON — treat as plain text.
    return content.slice(0, maxLen).trim();
  }

  const parts: string[] = [];
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (typeof node.text === 'string') parts.push(node.text);
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(doc);

  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

/** Build a list item (meta + preview) from a full local entry. */
function toListItem(entry: LocalJournalEntry): EntryListItem {
  return {
    entryDate: entry.entryDate,
    syncStatus: entry.syncStatus,
    version: entry.version,
    updatedAt: entry.updatedAt,
    preview: extractPreview(entry.content),
  };
}

/** Persist an entry to IndexedDB, swallowing errors (keeps UI responsive). */
async function persistEntry(entry: LocalJournalEntry): Promise<void> {
  try {
    await dbPutEntry(entry);
  } catch {
    // Storage full or IndexedDB unavailable — in-memory state still holds.
  }
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useEntryStore = create<EntryStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  currentEntry: null,
  entries: [],
  isLoading: false,
  error: null,
  calendarDates: [],

  // ── Actions ────────────────────────────────────────────────────────────────
  setCurrentEntry: (entry) => {
    set({ currentEntry: entry });
  },

  loadEntries: async () => {
    set({ isLoading: true, error: null });
    try {
      const all = await getAllEntries();
      set({
        entries: all.map(toListItem),
        calendarDates: all.map((e) => e.entryDate),
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load entries';
      // IndexedDB unavailable (e.g. jsdom without polyfill) — keep in-memory list.
      set({ isLoading: false, error: message });
    }
  },

  openEntry: async (entryDate) => {
    set({ error: null });
    try {
      const existing = await getEntry(entryDate);
      if (existing) {
        set({ currentEntry: existing });
        return;
      }
    } catch {
      // fall through to in-memory lookup
    }

    // Not in DB — if we already have it in memory, keep it; otherwise create fresh.
    const inMemory = get().currentEntry?.entryDate === entryDate ? get().currentEntry : null;
    if (inMemory) {
      set({ currentEntry: inMemory });
      return;
    }
    await get().createEntry(entryDate);
  },

  createEntry: async (entryDate, content = '') => {
    // Idempotent: if an entry already exists, open it instead of overwriting.
    try {
      const existing = await getEntry(entryDate);
      if (existing) {
        set({ currentEntry: existing });
        return;
      }
    } catch {
      // IndexedDB not available in this environment — fall back to memory check.
      const existingMeta = get().entries.find((e) => e.entryDate === entryDate);
      if (existingMeta && get().currentEntry?.entryDate === entryDate) {
        return;
      }
    }

    const now = new Date().toISOString();
    const newEntry: LocalJournalEntry = {
      entryDate,
      content,
      version: 0,
      localVersion: 1,
      updatedAt: now,
      createdAt: now,
      mediaRefs: [],
      // Queued for sync, not yet server-confirmed. Local-first: the write still
      // succeeds offline; the ExtendedSyncQueue will push it when online.
      syncStatus: SyncStatus.Pending,
      contentHash: '',
      pendingMediaUploads: [],
    };

    const previousEntries = get().entries;
    const withoutDup = previousEntries.filter((e) => e.entryDate !== entryDate);

    set({
      currentEntry: newEntry,
      entries: [toListItem(newEntry), ...withoutDup],
      calendarDates: Array.from(new Set([...get().calendarDates, entryDate])),
      error: null,
    });

    await persistEntry(newEntry);
    await enqueueEntrySync(entryDate, {
      content: newEntry.content,
      version: newEntry.version,
      mediaRefs: newEntry.mediaRefs,
    });
  },

  updateEntry: async (entryDate, content) => {
    const current = get().currentEntry;
    if (!current || current.entryDate !== entryDate) return;

    const updatedEntry: LocalJournalEntry = {
      ...current,
      content,
      localVersion: current.localVersion + 1,
      updatedAt: new Date().toISOString(),
      // Queued for sync, not yet server-confirmed.
      syncStatus: SyncStatus.Pending,
    };

    // Optimistic in-memory update (incl. refreshed preview in the list).
    set({
      currentEntry: updatedEntry,
      entries: get().entries.map((e) =>
        e.entryDate === entryDate ? toListItem(updatedEntry) : e
      ),
      error: null,
    });

    await persistEntry(updatedEntry);
    await enqueueEntrySync(entryDate, {
      content: updatedEntry.content,
      version: updatedEntry.version,
      mediaRefs: updatedEntry.mediaRefs,
    });
  },

  deleteEntry: async (entryDate) => {
    const previousEntries = get().entries;
    const previousCurrentEntry = get().currentEntry;
    const previousCalendarDates = get().calendarDates;

    set({
      entries: previousEntries.filter((e) => e.entryDate !== entryDate),
      currentEntry:
        previousCurrentEntry?.entryDate === entryDate ? null : previousCurrentEntry,
      calendarDates: previousCalendarDates.filter((d) => d !== entryDate),
      error: null,
    });

    try {
      await dbDeleteEntry(entryDate);
    } catch {
      // Local-first: the in-memory deletion stands even if the IndexedDB write
      // fails (e.g. storage unavailable). We do not resurrect a deleted entry.
    }

    // Enqueue the delete so it propagates to the server (DELETE /entries/{date}).
    await enqueueEntryDelete(entryDate);
  },

  loadCalendarDates: async (year, month) => {
    try {
      const all = await getAllEntries();
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates = all
        .map((e) => e.entryDate)
        .filter((d) => d.startsWith(prefix));
      // Merge with any dates already known (don't drop other months).
      set({
        calendarDates: Array.from(new Set([...get().calendarDates, ...dates])),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load calendar dates';
      set({ error: message });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
